import { Button, Card, Modal, Space, Tag, Tooltip } from 'antd';
import { observer } from 'mobx-react';
import React, { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import * as Component from '../../../../shared/components';
import ActionHistoryFilter from '../components/ActionHistoryFilter';
import {
  formatActionText,
  formatChangedFields,
  formatDataPreview,
  formatObjectTypeText,
  getActionColor,
  handleActionHistoryError,
} from '../handlers/actionHistoryHandlers';
import { actionHistoryService } from '../service/actionHistoryService';
import { ActionHistoryFilterType, ActionHistoryType } from '../types/actionHistoryType';

const ActionHistoryPage: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [detailModalVisible, setDetailModalVisible] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState<ActionHistoryType | null>(null);

  const fetchActionHistory = useCallback(
    async (params: URLSearchParams): Promise<{ data: ActionHistoryType[]; count: number }> => {
      try {
        const response = await actionHistoryService.fetchActionHistoryList(params);
        return { data: response.data, count: response.count };
      } catch (error) {
        handleActionHistoryError(error);
        return { data: [], count: 0 };
      }
    },
    []
  );

  const showDetailModal = useCallback((record: ActionHistoryType) => {
    // 이미 받아온 데이터를 사용하므로 추가 fetch 불필요
    setSelectedRecord(record);
    setDetailModalVisible(true);
  }, []);

  const handleFilter = (filters: ActionHistoryFilterType) => {
    const newParams = new URLSearchParams(searchParams);

    // 기존 필터 파라미터 제거
    newParams.delete('action');
    newParams.delete('object_type');
    newParams.delete('start_date');
    newParams.delete('end_date');

    // 새로운 필터 파라미터 추가
    if (filters.action) newParams.set('action', filters.action);
    if (filters.object_type) newParams.set('object_type', filters.object_type);
    if (filters.start_date) newParams.set('start_date', filters.start_date);
    if (filters.end_date) newParams.set('end_date', filters.end_date);

    // 페이지를 1로 리셋
    newParams.set('page', '1');

    setSearchParams(newParams);
  };

  const handleResetFilter = () => {
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('action');
    newParams.delete('object_type');
    newParams.delete('start_date');
    newParams.delete('end_date');
    newParams.set('page', '1');
    setSearchParams(newParams);
  };

  const columns = [
    {
      title: '사용자',
      dataIndex: 'user_username',
      key: 'user_username',
      render: (_text: string, record: ActionHistoryType): React.ReactNode => {
        return (
          <div>
            <div>{record.user_username}</div>
            <div style={{ fontSize: '12px', color: '#666' }}>{record.user_email}</div>
          </div>
        );
      },
    },
    {
      title: '액션',
      dataIndex: 'action',
      key: 'action',
      render: (_text: string, record: ActionHistoryType): React.ReactNode => {
        return <Tag color={getActionColor(record.action)}>{formatActionText(record.action)}</Tag>;
      },
    },
    {
      title: '객체 타입',
      dataIndex: 'object_type_name',
      key: 'object_type_name',
      render: (_text: string, record: ActionHistoryType): React.ReactNode => {
        return <div>{formatObjectTypeText(record.object_type_name)}</div>;
      },
    },
    {
      title: '객체 제목',
      dataIndex: 'object_title',
      key: 'object_title',
      render: (_text: string, record: ActionHistoryType): React.ReactNode => {
        return <div>{record.object_title}</div>;
      },
    },
    {
      title: '객체 ID',
      dataIndex: 'object_id',
      key: 'object_id',
    },
    {
      title: '변경된 필드',
      dataIndex: 'changed_fields',
      key: 'changed_fields',
      render: (_text: string, record: ActionHistoryType): React.ReactNode => {
        return (
          <Tooltip title={formatChangedFields(record.changed_fields)}>
            <div style={{ maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
              {formatChangedFields(record.changed_fields)}
            </div>
          </Tooltip>
        );
      },
    },
    {
      title: '상태 코드',
      dataIndex: 'status_code',
      key: 'status_code',
      render: (_text: string, record: ActionHistoryType): React.ReactNode => {
        return record.status_code ? (
          <Tag color={record.status_code >= 400 ? 'red' : 'green'}>{record.status_code}</Tag>
        ) : (
          <span>-</span>
        );
      },
    },
    {
      title: '생성일',
      dataIndex: 'created_at',
      key: 'created_at',
      render: (_text: string, record: ActionHistoryType): React.ReactNode => {
        return <div>{new Date(record.created_at).toLocaleString('ko-KR')}</div>;
      },
    },
    {
      title: 'Actions',
      key: 'action',
      dataIndex: 'action',
      render: (_text: string, record: ActionHistoryType): React.ReactNode => (
        <Space size="middle">
          <Button size="small" onClick={() => showDetailModal(record)}>
            상세보기
          </Button>
        </Space>
      ),
    },
  ];

  return (
    <Component.BasePageComponent>
      <Card style={{ marginBottom: '16px' }}>
        <ActionHistoryFilter onFilter={handleFilter} onReset={handleResetFilter} />
      </Card>

      <Component.TableComponent<ActionHistoryType>
        name="Action History"
        searchParams={searchParams}
        setSearchParams={setSearchParams}
        columns={columns}
        dataFetch={fetchActionHistory}
      />

      <Modal
        title="액션 히스토리 상세"
        open={detailModalVisible}
        onCancel={() => setDetailModalVisible(false)}
        footer={[
          <Button key="close" onClick={() => setDetailModalVisible(false)}>
            닫기
          </Button>,
        ]}
        width={800}
      >
        {selectedRecord && (
          <div>
            <div style={{ marginBottom: '16px' }}>
              <h4>기본 정보</h4>
              <p>
                <strong>사용자:</strong> {selectedRecord.user_username} ({selectedRecord.user_email}
                )
              </p>
              <p>
                <strong>액션:</strong>{' '}
                <Tag color={getActionColor(selectedRecord.action)}>
                  {formatActionText(selectedRecord.action)}
                </Tag>
              </p>
              <p>
                <strong>객체 타입:</strong> {formatObjectTypeText(selectedRecord.object_type_name)}
              </p>
              <p>
                <strong>객체 제목:</strong> {selectedRecord.object_title}
              </p>
              <p>
                <strong>객체 ID:</strong> {selectedRecord.object_id}
              </p>
              <p>
                <strong>생성일:</strong>{' '}
                {new Date(selectedRecord.created_at).toLocaleString('ko-KR')}
              </p>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <h4>변경된 필드</h4>
              <pre style={{ background: '#f5f5f5', padding: '8px', borderRadius: '4px' }}>
                {formatChangedFields(selectedRecord.changed_fields)}
              </pre>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <h4>이전 데이터</h4>
              <pre
                style={{
                  background: '#f5f5f5',
                  padding: '8px',
                  borderRadius: '4px',
                  maxHeight: '200px',
                  overflow: 'auto',
                }}
              >
                {formatDataPreview(selectedRecord.previous_data)}
              </pre>
            </div>

            <div style={{ marginBottom: '16px' }}>
              <h4>새 데이터</h4>
              <pre
                style={{
                  background: '#f5f5f5',
                  padding: '8px',
                  borderRadius: '4px',
                  maxHeight: '200px',
                  overflow: 'auto',
                }}
              >
                {formatDataPreview(selectedRecord.new_data)}
              </pre>
            </div>
          </div>
        )}
      </Modal>
    </Component.BasePageComponent>
  );
};

export default observer(ActionHistoryPage);
